import { SignalingClient, SignalMessage } from './index';

export class PollSignalingClient implements SignalingClient {
  private handlers = new Set<(msg: SignalMessage) => void | Promise<void>>();
  private pollInterval?: any;
  private roomId?: string;
  private peerId?: string;
  private password?: string;
  private token?: string;
  private baseUrl: string;
  // Cursor for at-least-once delivery: the highest message seq we've received. The
  // server returns messages with seq > cursor (non-destructively), so a dropped poll
  // response is re-delivered on the next tick instead of being lost.
  private cursor = 0;

  constructor(options: { baseUrl?: string; roomId: string; peerId: string; password?: string }) {
    this.roomId = options.roomId;
    this.peerId = options.peerId;
    this.password = options.password;
    this.baseUrl = options.baseUrl || '/api/signaling';
  }

  /** Auth + room secret travel in headers, never the query string (keeps them out of access logs). */
  private authHeaders(): Record<string, string> {
    const h: Record<string, string> = {};
    if (this.token) h['Authorization'] = `Bearer ${this.token}`;
    if (this.password) h['X-Room-Password'] = this.password;
    return h;
  }

  async connect(token: string): Promise<void> {
    this.token = token;
    if (this.pollInterval) return;

    // Start short-poll interval requests
    this.pollInterval = setInterval(async () => {
      try {
        const res = await fetch(
          `${this.baseUrl}/poll?roomId=${encodeURIComponent(this.roomId || '')}&peerId=${encodeURIComponent(this.peerId || '')}&cursor=${this.cursor}`,
          { headers: this.authHeaders() }
        );
        if (res.ok) {
          const data = await res.json();
          const messages = data.messages || [];
          for (const msg of messages) {
            for (const handler of this.handlers) {
              await Promise.resolve(handler(msg));
            }
          }
          // Advance the cursor only after dispatching, so a failed fetch re-delivers.
          if (typeof data.cursor === 'number') {
            this.cursor = data.cursor;
            this.sendAck(data.cursor);
          }
        } else {
          console.warn(`PollSignalingClient: poll request failed with status ${res.status}`);
        }
      } catch (err) {
        console.warn('PollSignalingClient: failed to retrieve messages:', err);
      }
    }, 1200);
  }

  private sendAck(cursor: number): void {
    fetch(
      `${this.baseUrl}/ack?peerId=${encodeURIComponent(this.peerId || '')}&cursor=${cursor}`,
      {
        method: 'POST',
        headers: this.authHeaders(),
      }
    ).then((res) => {
      if (!res.ok) {
        console.warn(`PollSignalingClient: acknowledge request failed with status ${res.status}`);
      }
    }).catch((err) => {
      console.warn('PollSignalingClient: failed to acknowledge messages:', err);
    });
  }

  send(msg: SignalMessage): void {
    // Inject peerId as "from" so perfect-negotiation can resolve polite/impolite roles.
    // Password is sent as a header, not in the body.
    const msgWithFrom = { ...msg, from: this.peerId };

    fetch(`${this.baseUrl}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.authHeaders(),
      },
      body: JSON.stringify(msgWithFrom),
    }).then((res) => {
      if (!res.ok) {
        console.warn(`PollSignalingClient: transmit request failed with status ${res.status}`);
      }
    }).catch((err) => {
      console.warn('PollSignalingClient: failed to transmit message:', err);
    });
  }

  on(handler: (msg: SignalMessage) => void | Promise<void>): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  close(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
  }

  async getParticipants(roomId: string): Promise<string[]> {
    try {
      const res = await fetch(
        `${this.baseUrl}/participants?roomId=${encodeURIComponent(roomId)}`,
        { headers: this.authHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        return data.participants || [];
      } else {
        console.warn(`PollSignalingClient: query participants request failed with status ${res.status}`);
      }
    } catch (err) {
      console.warn('PollSignalingClient: failed to query participants:', err);
    }
    return [];
  }

  joinRoom(_roomId: string): void {
    // Poll-based signaling does room presence through /poll + /participants; no extra join verb.
  }

  leaveRoom(_roomId: string): void {
    // No-op: closing the polling client and graceful `leave` signaling handle cleanup.
  }
}
